import { useEffect, useRef } from "react";
import { getWebApp, isInsideTelegram } from "./telegram";

/**
 * Shows Telegram's native BackButton while the component is mounted.
 * The latest `onBack` is always used (kept in a ref), so a page can change
 * what "back" means — e.g. close a bottom sheet first — without rebinding.
 */
export function useBackButton(onBack: () => void, enabled = true) {
  const handlerRef = useRef(onBack);
  handlerRef.current = onBack;

  useEffect(() => {
    const tg = getWebApp();
    if (!enabled || !tg || !isInsideTelegram()) return;
    const handler = () => handlerRef.current();
    tg.BackButton.onClick(handler);
    tg.BackButton.show();
    return () => {
      tg.BackButton.offClick(handler);
      tg.BackButton.hide();
    };
  }, [enabled]);
}

/** True only in a plain browser tab (dev/testing), where Telegram's native buttons don't exist. */
export function useIsBrowserFallback(): boolean {
  return !isInsideTelegram();
}

/**
 * Drives Telegram's native MainButton. Bound once per visibility change and
 * updated in place afterwards — no hide/show flicker on every render, and
 * the click handler always sees the latest state via a ref.
 */
export function useMainButton(
  text: string,
  onClick: () => void,
  opts?: { disabled?: boolean; loading?: boolean; hidden?: boolean },
) {
  const handlerRef = useRef(onClick);
  handlerRef.current = onClick;
  const hidden = !!opts?.hidden;
  const disabled = !!opts?.disabled;
  const loading = !!opts?.loading;

  useEffect(() => {
    const tg = getWebApp();
    if (hidden || !tg || !isInsideTelegram()) return;
    const handler = () => {
      if (!tg.MainButton.isActive) return;
      handlerRef.current();
    };
    tg.MainButton.onClick(handler);
    tg.MainButton.show();
    return () => {
      tg.MainButton.offClick(handler);
      tg.MainButton.hideProgress();
      tg.MainButton.hide();
    };
  }, [hidden]);

  useEffect(() => {
    const tg = getWebApp();
    if (hidden || !tg || !isInsideTelegram()) return;
    tg.MainButton.setText(text);
    if (disabled || loading) tg.MainButton.disable();
    else tg.MainButton.enable();
    if (loading) tg.MainButton.showProgress(false);
    else tg.MainButton.hideProgress();
  }, [text, disabled, loading, hidden]);
}
